import React from 'react';
import './Icon.css';

// ONE icon component for the whole product.  <Icon name="streak" />  /  <Icon name="streak" art size={56} />
//
// Two tiers, because the supplied library is illustrations, not UI glyphs:
//   • GLYPHS  monochrome stroke icons on a 24px grid, tinted by `currentColor`. Used for everything
//             functional: navigation, buttons, list rows, status, form controls.
//   • ART     the supplied multi-colour illustrations (client/public/art/icons/*.svg — the originals live in
//             /zaptude_icons_svg and are cleaned by scripts/prepare_svg_assets.js). Used for feature moments:
//             streak, achievement, XP, empty states, results. `art` falls back to the glyph when a name has no
//             illustration, so a call site never has to know which exist.
// Emoji are not used as UI icons anywhere in the app.
const PUBLIC = process.env.PUBLIC_URL || '';

const G = {
  home: <><path d="M3 11.5 12 4l9 7.5" /><path d="M5.5 10.5V20H10v-5h4v5h4.5v-9.5" /></>,
  practice: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
  bolt: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
  progress: <><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></>,
  'trend-up': <><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></>,
  'trend-down': <><path d="M3 7l6 6 4-4 8 8" /><path d="M15 17h6v-6" /></>,
  'trend-flat': <path d="M4 12h16M15 7l5 5-5 5" />,
  placement: <><rect x="3" y="7" width="18" height="13" rx="2.5" /><path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" /><path d="M3 13h18" /></>,
  notification: <><path d="M6 9a6 6 0 1 1 12 0c0 5 2 6.5 2 6.5H4S6 14 6 9z" /><path d="M10 19a2 2 0 0 0 4 0" /></>,
  feedback: <><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12z" /><path d="M8.5 12h.01M12 12h.01M15.5 12h.01" /></>,
  achievement: <><circle cx="12" cy="9" r="5.5" /><path d="M8.5 13.8 7 21l5-2.7 5 2.7-1.5-7.2" /></>,
  streak: <path d="M12 2.5c.8 3.5-3.5 5.2-3.5 9.5a3.5 3.5 0 0 0 7 0c0-1 0-1.8-.7-3 2.5 1 4.2 3.2 4.2 6a7 7 0 0 1-14 0c0-5.2 4.5-7 7-12.5z" />,
  xp: <path d="m12 3 2.6 5.6 6.1.8-4.5 4.2 1.2 6L12 16.6 6.6 19.6l1.2-6L3.3 9.4l6.1-.8z" />,
  skill: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r=".8" fill="currentColor" /></>,
  mistake: <><path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3 9" /><path d="M3 4v5h5" /></>,
  announcement: <><path d="M3 11v3a1.5 1.5 0 0 0 1.5 1.5H6L10.5 19V6L6 9.5H4.5A1.5 1.5 0 0 0 3 11z" /><path d="M14.5 8.5a5 5 0 0 1 0 7" /><path d="M17.5 6a8.5 8.5 0 0 1 0 12" /></>,
  logout: <><path d="M9 4H6.5A2.5 2.5 0 0 0 4 6.5v11A2.5 2.5 0 0 0 6.5 20H9" /><path d="M16 8l4 4-4 4M20 12H9" /></>,
  settings: <><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></>,
  profile: <><circle cx="12" cy="8" r="4" /><path d="M4.5 20.5c.7-3.8 3.7-6 7.5-6s6.8 2.2 7.5 6" /></>,
  users: <><circle cx="9" cy="8.5" r="3.5" /><path d="M2.5 20c.5-3.5 3-5.5 6.5-5.5s6 2 6.5 5.5M16 5.2a3.3 3.3 0 0 1 0 6.6M18.5 14.7c1.6.7 2.7 2.4 3 4.8" /></>,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  'chevron-right': <path d="M9 5l7 7-7 7" />,
  'chevron-left': <path d="M15 5l-7 7 7 7" />,
  'chevron-down': <path d="M5 9l7 7 7-7" />,
  'chevron-up': <path d="M5 15l7-7 7 7" />,
  'arrow-right': <path d="M4 12h16M14 6l6 6-6 6" />,
  lock: <><rect x="4.5" y="10.5" width="15" height="10" rx="2.5" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></>,
  key: <><circle cx="8" cy="15" r="4" /><path d="M11 12l9-9M16 7l3 3" /></>,
  shield: <path d="M12 3 4.5 6v5.5c0 4.5 3.2 8 7.5 9.5 4.3-1.5 7.5-5 7.5-9.5V6z" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  timer: <><circle cx="12" cy="13.5" r="7.5" /><path d="M12 9.5v4l2.5 1.5M9.5 2.5h5" /></>,
  calendar: <><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" /><path d="M8 3v4M16 3v4M3.5 10h17" /></>,
  trophy: <><path d="M8 4h8v5a4 4 0 0 1-8 0V4z" /><path d="M8 6H5a2 2 0 0 0 2 4M16 6h3a2 2 0 0 1-2 4" /><path d="M12 13v4M8.5 20h7M10 17h4" /></>,
  level: <><path d="M6.5 4h11l4 5.5L12 21 2.5 9.5z" /><path d="M2.5 9.5h19M9 4l3 5.5L15 4" /></>,
  mission: <path d="M5 21V4M5 4h11l-2 4 2 4H5" />,
  readiness: <><path d="M4 16a8 8 0 1 1 16 0" /><path d="M12 16l4-5" /><circle cx="12" cy="16" r="1" fill="currentColor" /></>,
  test: <><path d="M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
  book: <><path d="M5 4.5A2.5 2.5 0 0 1 7.5 2H19v17H7.5A2.5 2.5 0 0 0 5 21.5z" /><path d="M5 19.5A2.5 2.5 0 0 1 7.5 17H19" /></>,
  building: <><rect x="5" y="3" width="14" height="18" rx="1.5" /><path d="M9 8h.01M12 8h.01M15 8h.01M9 12h.01M12 12h.01M15 12h.01M10 21v-4h4v4" /></>,
  warning: <><path d="M12 4 2.8 19.5h18.4z" /><path d="M12 10v4.5M12 17.4h.01" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
  success: <><circle cx="12" cy="12" r="9" /><path d="M8 12.5l3 3 5-6" /></>,
  error: <><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></>,
  skipped: <><circle cx="12" cy="12" r="9" /><path d="M8 12h8" /></>,
  'wifi-off': <><path d="M2 8.8a15 15 0 0 1 4.2-2.7M22 8.8A15 15 0 0 0 10 5.3M5 12.5a10 10 0 0 1 3.5-2M19 12.5a10 10 0 0 0-3-1.8M8.5 16a5 5 0 0 1 7 0" /><path d="M12 19.5h.01M3 3l18 18" /></>,
  refresh: <><path d="M20 12a8 8 0 1 1-2.4-5.7L20 8.5" /><path d="M20 4v4.5h-4.5" /></>,
  send: <path d="M21 3 10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5z" />,
  eye: <><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  'eye-off': <><path d="M10.6 5.1A9.7 9.7 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3 3.8M6.2 6.6A17 17 0 0 0 2 12s3.6 7 10 7c1.6 0 3-.4 4.3-1" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>,
  inbox: <><path d="M3 13l3-8h12l3 8v6a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 19z" /><path d="M3 13h5l1.5 2.5h5L16 13h5" /></>,
  pin: <path d="M12 17v5M8 3h8l-1 6 3 4H6l3-4z" />,
  tip: <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.4 1.1 2.2h5c0-.8.4-1.6 1.1-2.2A6 6 0 0 0 12 3z" />,
  sparkle: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" />,
  edit: <><path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></>,
  list: <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />,
  grid: <><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  chart: <><path d="M3 20h18" /><path d="M6 20v-6M11 20V8M16 20v-9M21 20V4" /></>,
  flag: <path d="M5 21V4M5 4h11l-2 4 2 4H5" />,
  lightning: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
  dot: <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />,
  more: <path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth="3" />,
};

// name -> file in public/art/icons (without .svg). Only names with a real illustration are listed.
const ART = {
  practice: 'practice', progress: 'statistics', chart: 'statistics', analytics: 'analytics', growth: 'growth', decline: 'decline',
  streak: 'streak', xp: 'energy', energy: 'energy', speed: 'speed', timer: 'time', clock: 'time',
  achievement: 'trophy', trophy: 'trophy', badge: 'badge', target: 'target', skill: 'intelligence', intelligence: 'intelligence',
  mistake: 'incorrect', test: 'testing', exam: 'exam', papers: 'papers', document: 'document', question: 'question',
  rocket: 'rocket-launch', security: 'security', students: 'students', users: 'students', diagnostics: 'diagnostics', global: 'global',
  writing: 'writing', law: 'law', batch: 'batch-processing', lightning: 'energy', bolt: 'energy', readiness: 'diagnostics',
};

export const ICON_NAMES = Object.keys(G);
export const ART_NAMES = Object.keys(ART);
export const hasArt = (name) => Object.prototype.hasOwnProperty.call(ART, name);
export const artSrc = (name) => (hasArt(name) ? `${PUBLIC}/art/icons/${ART[name]}.svg` : null);

/**
 * @param {string}  name   icon name (see ICON_NAMES / ART_NAMES)
 * @param {number}  size   px, default 20
 * @param {boolean} art    prefer the illustration when one exists (falls back to the glyph)
 * @param {string}  title  accessible name; without it the icon is decorative (aria-hidden)
 * @param {boolean} eager  art only: load immediately (above-the-fold hero art). Default lazy.
 */
export default function Icon({ name, size = 20, art = false, title, eager = false, className = '', strokeWidth = 2, ...rest }) {
  const label = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true, focusable: 'false' };
  if (art && hasArt(name)) {
    return (
      <img
        className={`z-icon z-icon--art ${className}`.trim()}
        src={artSrc(name)}
        width={size}
        height={size}
        alt={title || ''}
        aria-hidden={title ? undefined : true}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        {...rest}
      />
    );
  }
  const glyph = G[name];
  if (!glyph) {
    if (process.env.NODE_ENV !== 'production') console.warn(`<Icon>: unknown icon "${name}"`);
    return null;
  }
  return (
    <svg
      className={`z-icon ${className}`.trim()}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...label}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {glyph}
    </svg>
  );
}
